import React, { useState, useEffect, useRef } from "react";

import FIcon from "@components/FIcon";

interface Props {
  className?: string;
  name?: string;
  selectedValue?: string | number;
  items: {
    label: string;
    value?: any;
    disabled?: boolean;
    dropdownOptions?: {
      label: string;
      value: string;
    }[];
  }[];
  onChange?: (value: string | number) => void;
}

const SubNavMobile = ({
  className = "",
  items = [],
  onChange,
  selectedValue,
}: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const getActiveItem = () => {
    return items.find(
      (item) =>
        item.value === selectedValue ||
        (item.dropdownOptions &&
          item.dropdownOptions.some((opt) => opt.value === selectedValue))
    );
  };

  const activeItem = getActiveItem();

  return (
    <div className={`${className} relative`} ref={menuRef}>
      <div className="flex items-center justify-between py-3 pb-[14px] border-b border-light_border">
        <h1 className="text-lg font-medium text-dark_night_sky pt-[16px]">
          {activeItem?.label || "Menu"}
        </h1>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="flex items-center justify-center h-[40px] w-[40px] rounded-lg hover:bg-neutral_100 transition-colors"
          aria-label="Toggle menu"
        >
          <div className="flex flex-col gap-[4px]">
            <div className="bg-dark_night_sky w-[14px] h-[1px]"></div>
            <div className="bg-dark_night_sky w-[14px] h-[1px]"></div>
            <div className="bg-dark_night_sky w-[14px] h-[1px]"></div>
          </div>
        </button>
      </div>

      {isMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-white border border-neutral_400 rounded-lg shadow-lg z-50 mt-1">
          <div className="py-2">
            {items.map((item, index) => {
              const { dropdownOptions } = item;

              const hasDropdown = dropdownOptions?.length > 0;

              const isActive =
                item.value === selectedValue ||
                (hasDropdown &&
                  item.dropdownOptions.some(
                    (opt) => opt.value === selectedValue
                  ));

              return (
                <div key={`${item.value}-${index}`}>
                  {hasDropdown ? (
                    <div className="px-4 py-2">
                      <div className="flex items-center justify-between">
                        <span
                          className={`font-medium ${isActive ? "text-dark_night_sky" : "text-neutral_600"}`}
                        >
                          {item.label}
                        </span>
                        <FIcon icon="chevron-right" size={16} color="#000F45" />
                      </div>
                      <div className="pl-4 mt-2 space-y-2">
                        {dropdownOptions.map((option, optionIndex) => (
                          <p
                            key={`${option.value}-${optionIndex}`}
                            className={`block py-2 text-sm ${
                              option.value === selectedValue
                                ? "bg-neutral_100 text-dark_night_sky font-medium px-2 rounded"
                                : "text-neutral_600"
                            }`}
                            onClick={(e) => {
                              e.preventDefault();

                              setIsMenuOpen(null);

                              onChange(`${item.value}/${option.value}`);
                            }}
                          >
                            {option.label}
                          </p>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button
                      className={`w-full text-left px-4 py-2 ${
                        isActive
                          ? "bg-neutral_100 text-dark_night_sky font-medium"
                          : "text-neutral_600"
                      }`}
                      onClick={() => {
                        if (item.disabled) return;
                        if (onChange) onChange(item.value);
                        setIsMenuOpen(false);
                      }}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubNavMobile;
